import { useState, useEffect, useRef } from "react";
import CoverPage from "./components/CoverPage";
import SceneryPage from "./components/SceneryPage";
import CulturePage from "./components/CulturePage";
import FoodPage from "./components/FoodPage";
import CityPage from "./components/CityPage";
import QuizPage from "./components/QuizPage";
import EndingPage from "./components/EndingPage";
import LoadingScreen from "./components/LoadingScreen";

const PAGE_COUNT = 7;
const MUSIC_URL = "https://storage.readdy-site.link/project_files/711f48f3-73fe-45ee-bd2f-4abeb8a2b702/20885db3-7231-4df3-9382-0f7c7bdbea0c_---.mp3?v=e2c4a0729596dfc9318238e919fc0302";

export default function Home() {
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicStarted, setMusicStarted] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Init audio
  useEffect(() => {
    const audio = new Audio(MUSIC_URL);
    audio.loop = true;
    audio.volume = 0.5;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Auto-play after loading completes
  const handleLoadingComplete = () => {
    setLoading(false);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          setMusicStarted(true);
        }).catch(() => {
          // Autoplay blocked, user needs to click
        });
      }
    }, 500);
  };

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        setMusicStarted(true);
      });
    }
  };

  const goToPage = (index: number) => {
    if (isAnimating || index === currentPage || index < 0 || index >= PAGE_COUNT) return;
    setIsAnimating(true);
    setCurrentPage(index);
    setTimeout(() => setIsAnimating(false), 900);
  };

  const goNext = () => goToPage(currentPage + 1);
  const goPrev = () => goToPage(currentPage - 1);
  const goRestart = () => goToPage(0);

  // Wheel scroll
  useEffect(() => {
    let lastWheel = 0;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const now = Date.now();
      if (now - lastWheel < 800) return;
      lastWheel = now;
      if (e.deltaY > 30) goNext();
      else if (e.deltaY < -30) goPrev();
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [currentPage, isAnimating]);

  // Touch support
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
      touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartY.current === null) return;
      const dy = touchStartY.current - e.changedTouches[0].clientY;
      const dx = touchStartX.current! - e.changedTouches[0].clientX;
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 50) {
        if (dy > 0) goNext();
        else goPrev();
      }
      touchStartY.current = null;
      touchStartX.current = null;
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [currentPage, isAnimating]);

  // Keyboard
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "ArrowRight") goNext();
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [currentPage, isAnimating]);

  // Dark pages: CityPage(4) and EndingPage(6)
  const isDarkPage = currentPage === 4 || currentPage === 6;

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Loading Screen */}
      {loading && <LoadingScreen onComplete={handleLoadingComplete} />}

      {/* Pages Container */}
      <div
        className="absolute inset-0 transition-transform duration-700 ease-in-out"
        style={{ transform: `translateY(-${currentPage * 100}%)` }}
      >
        {/* Page 1: Cover */}
        <div className="w-full h-screen">
          <CoverPage onEnter={goNext} />
        </div>

        {/* Page 2: Scenery */}
        <div className="w-full h-screen">
          <SceneryPage visible={currentPage === 1} onNext={goNext} onPrev={goPrev} />
        </div>

        {/* Page 3: Culture */}
        <div className="w-full h-screen">
          <CulturePage visible={currentPage === 2} onNext={goNext} onPrev={goPrev} />
        </div>

        {/* Page 4: Food */}
        <div className="w-full h-screen">
          <FoodPage visible={currentPage === 3} onNext={goNext} onPrev={goPrev} />
        </div>

        {/* Page 5: City */}
        <div className="w-full h-screen">
          <CityPage visible={currentPage === 4} onNext={goNext} onPrev={goPrev} />
        </div>

        {/* Page 6: Quiz */}
        <div className="w-full h-screen">
          <QuizPage visible={currentPage === 5} onNext={goNext} onPrev={goPrev} />
        </div>

        {/* Page 7: Ending */}
        <div className="w-full h-screen">
          <EndingPage visible={currentPage === 6} onPrev={goPrev} onRestart={goRestart} />
        </div>
      </div>

      {/* Side Page Indicator */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-3">
        {Array.from({ length: PAGE_COUNT }).map((_, i) => (
          <button
            key={i}
            onClick={() => goToPage(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === currentPage
                ? `w-1.5 h-6 ${isDarkPage ? "bg-amber-400" : "bg-gray-700"}`
                : `w-1.5 h-1.5 ${isDarkPage ? "bg-white/30 hover:bg-white/60" : "bg-gray-400/50 hover:bg-gray-600"}`
            }`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-white/10">
        <div
          className="h-full bg-white/60 transition-all duration-700 ease-in-out"
          style={{ width: `${((currentPage + 1) / PAGE_COUNT) * 100}%` }}
        />
      </div>

      {/* Music Control Button */}
      {!loading && (
        <button
          onClick={toggleMusic}
          className="fixed bottom-6 left-6 z-50 w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 cursor-pointer group"
          style={{
            background: isDarkPage ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.15)",
            borderColor: isDarkPage ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
          }}
          title={isPlaying ? "暂停音乐" : "播放音乐"}
        >
          {/* Rotating vinyl disc when playing */}
          <div
            className={`absolute inset-0 rounded-full border-2 border-dashed transition-all duration-300 ${
              isPlaying ? "opacity-60 animate-spin" : "opacity-0"
            }`}
            style={{
              borderColor: isDarkPage ? "rgba(251,191,36,0.6)" : "rgba(255,255,255,0.5)",
              animationDuration: "4s",
            }}
          />
          <i
            className={`text-sm transition-all duration-200 ${
              isPlaying ? "ri-pause-line" : "ri-music-2-line"
            } ${isDarkPage ? "text-white/80" : "text-white"}`}
          />
          {/* Tooltip */}
          <span
            className="absolute left-12 whitespace-nowrap text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
            style={{
              background: isDarkPage ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.5)",
              color: "white",
            }}
          >
            {isPlaying ? "暂停" : (musicStarted ? "继续" : "播放")} · 晴天
          </span>
        </button>
      )}
    </div>
  );
}
